'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';

import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { DrawerHeader } from '@repo/ui/src/modules/UI';

import { AuthViews, contentDefaults } from '@repo/ui/src/modules/UI/constants';
import LoginCard from './LoginCard';
import CreateUser from './CreateUser';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';

interface Props {
  onSuccess?: () => Promise<void> | void;
  isCheckout?: boolean;
  view?: AuthViews;
}

const components = {
  [AuthViews.LOGIN]: LoginCard,
  [AuthViews.CREATE]: CreateUser,
  [AuthViews.FORGOT]: ForgotPassword,
  [AuthViews.RESET_PASSWORD]: ResetPassword,
};

const Auth = ({ onSuccess, isCheckout = false, view: initialView }: Props) => {
  const { t } = useTranslation();
  const [view, setView] = React.useState<AuthViews>(initialView ?? AuthViews.LOGIN);
  const [email, setEmail] = React.useState<string>('');

  React.useEffect(() => {
    setView(initialView ?? AuthViews.LOGIN);
  }, [initialView]);

  const { header, footer } = t(`auth.${view}`, undefined, {
    returnObjects: true,
    default: contentDefaults[view],
  }) as any;

  return (
    <>
      <DrawerHeader title={header?.title} />
      <div className="tw-full tw-px-6 tw-pb-10 sm:tw-px-16 sm:tw-pb-14 sm:tw-w-small">
        {header && (
          <>
            {(header?.subtitle || header?.description) && (
              <div className="tw-mb-7">
                {header.subtitle && (
                  <h6 className="tra-text-web-sub-2 tw-mb-4">{header?.subtitle}</h6>
                )}
                <p className="tra-text-web-body-l">{header?.description}</p>
              </div>
            )}
          </>
        )}
        {
          <DynamicComponent
            _componentTypes={components}
            _field="view"
            view={view}
            setView={setView}
            onSuccess={onSuccess}
            isCheckout={isCheckout}
            setEmail={setEmail}
            email={email}
          />
        }
        {footer && (
          <div className="tw-text-center">
            <p className="tra-text-web-body-m">
              <span>{footer?.text}&nbsp;</span>
              <button
                onClick={() => setView(footer?.goTo)}
                className="tra-text-web-body-m-link"
                tabIndex={0}
                type="button"
              >
                {footer?.title}
              </button>
            </p>
          </div>
        )}
        {isCheckout && (
          <div className="tw-mt-8">
            <h4 className="tra-text-web-heading-7 tw-mb-4">
              {t('auth.guestCheckout.header', undefined, { default: 'Guest Checkout' })}
            </h4>
            <p className="tra-text-web-body-m tw-mb-6">
              {t('auth.guestCheckout.description', undefined, {
                default:
                  'You can always check out without an account. You will have the option to create an account later',
              })}
            </p>
            <Button onClick={onSuccess} variant={ButtonVariant.secondary} type="button" full>
              {t('auth.guestCheckout.buttonText', undefined, { default: 'Checkout as guest' })}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default Auth;
